/**
 * UploadButton — the documents two-phase upload, inline (the `useS3Upload`
 * hook is avatar/logo-shaped; documents carry folder + title + tags, so we
 * drive the request→PUT→create sequence here). Supports multiple files.
 */
import * as React from "react";
import { Upload } from "lucide-react";
import { trpc } from "../../lib/trpc.ts";
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES } from "./lib/format.ts";

export interface UploadButtonProps {
  /** Current folder; uploads land here. Null = root. */
  folderId: string | null;
  /** Called after each successful create so the caller can refetch. */
  onUploaded: () => void;
}

export function UploadButton({ folderId, onUploaded }: UploadButtonProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const requestUpload = trpc.documents.requestUpload.useMutation();
  const create = trpc.documents.create.useMutation();

  const onChange = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      if (event.target) event.target.value = ""; // allow re-picking the same file
      if (files.length === 0) return;

      setError(null);
      setBusy(true);
      try {
        for (const file of files) {
          if (!DOCUMENT_ACCEPT.includes(file.type)) {
            setError(`"${file.name}" — unsupported file type.`);
            continue;
          }
          if (file.size > DOCUMENT_MAX_BYTES) {
            setError(`"${file.name}" exceeds the 50 MB limit.`);
            continue;
          }
          const presigned = await requestUpload.mutateAsync({
            originalName: file.name,
            contentType: file.type as never,
            sizeBytes: file.size,
          });
          const putRes = await fetch(presigned.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": file.type },
            body: file,
          });
          if (!putRes.ok) throw new Error(`Upload failed (${putRes.status}).`);
          await create.mutateAsync({
            objectKey: presigned.objectKey,
            title: "",
            originalName: file.name,
            contentType: file.type as never,
            sizeBytes: file.size,
            folderId,
            tags: [],
          });
          onUploaded();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
      } finally {
        setBusy(false);
      }
    },
    [folderId, requestUpload, create, onUploaded],
  );

  return (
    <div className="flex flex-col items-end gap-1">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={DOCUMENT_ACCEPT.join(",")}
        className="hidden"
        onChange={onChange}
        disabled={busy}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex h-9 items-center gap-1.5 rounded-sm bg-ink px-3.5 text-sm font-semibold text-paper hover:bg-ink-2 disabled:opacity-60"
      >
        <Upload size={14} strokeWidth={2.2} aria-hidden />
        {busy ? "Uploading…" : "Upload"}
      </button>
      {error && <span className="text-xs text-bad">{error}</span>}
    </div>
  );
}
